import {
  LoyaltyCustomerInfo,
  LoyaltyTierSystem,
  LoyaltyTierSystemTier,
} from "../loyaltyTypes";
import { hexToRgb } from "../help/colors/colors";

const DEFAULT_TIER_COLORS = ["#B26A58", "#848B98", "#E9AF2E"];

export const sortLoyaltyTiers = (
  tiers: LoyaltyTierSystemTier[] = []
): LoyaltyTierSystemTier[] =>
  [...tiers].sort((first, second) => first.tierOrder - second.tierOrder);

export const getCurrentLoyaltyTier = (
  customerInfo?: LoyaltyCustomerInfo | null,
  tierSystem?: LoyaltyTierSystem | null
): LoyaltyTierSystemTier | undefined => {
  const tiers = sortLoyaltyTiers(tierSystem?.tiers);
  const currentTierId = customerInfo?.tierProgress?.currentTierId;

  return tiers.find((tier) => tier.tierId === currentTierId) ?? tiers[0];
};

const getTierIndex = (
  tier: LoyaltyTierSystemTier,
  sortedTiers: LoyaltyTierSystemTier[]
): number =>
  Math.max(
    0,
    sortedTiers.findIndex((candidate) => candidate.tierId === tier.tierId)
  );

export const getTierColor = (
  tier: LoyaltyTierSystemTier,
  sortedTiers: LoyaltyTierSystemTier[]
): string => {
  const tierIndex = getTierIndex(tier, sortedTiers);

  return tier.color || DEFAULT_TIER_COLORS[tierIndex] || DEFAULT_TIER_COLORS[2];
};

/** 1-based position engraved on the tier badge, matching the Figma hexagon artwork. */
export const getTierRank = (
  tier: LoyaltyTierSystemTier,
  sortedTiers: LoyaltyTierSystemTier[]
): number => getTierIndex(tier, sortedTiers) + 1;

/**
 * Localized tier rules built by loyalty-service (`buildTierRulesLocalized`),
 * so the benefit copy shown to guests always comes from the service.
 */
export const getTierRules = (
  tier: LoyaltyTierSystemTier,
  locale: string
): string[] =>
  tier.rules?.[locale] ||
  tier.rules?.[locale.split("-")[0]] ||
  tier.rules?.en ||
  Object.values(tier.rules || {}).find((rules) => rules?.length) ||
  [];

export const getLocalizedTierText = (
  value: Record<string, string | undefined> | undefined,
  locale: string
): string =>
  value?.[locale] ||
  value?.[locale.split("-")[0]] ||
  value?.en ||
  Object.values(value || {})[0] ||
  "";

/**
 * Figma paints the medal's ribbon and the engraved rank in the tier colour under
 * a 30% black layer (15404:110643). Compositing to one fill here keeps the
 * glyph's anti-aliased edges clean, which stacking two draws would not.
 */
export const shadeTierColor = (color: string, amount = 0.3): string => {
  const channels = hexToRgb(color);

  if (!channels) {
    return color;
  }

  const shaded = [channels.red, channels.green, channels.blue].map((channel) =>
    Math.round(channel * (1 - amount))
  );

  return `rgb(${shaded.join(", ")})`;
};

// ----------------------Composited in JS rather than with color-mix, which the older mobile browsers we serve do not support-----------------------
export const tintTierColor = (color: string, amount = 0.9): string => {
  const channels = hexToRgb(color);

  if (!channels) {
    return color;
  }

  const tinted = [channels.red, channels.green, channels.blue].map((channel) =>
    Math.round(channel + (255 - channel) * amount)
  );

  return `rgb(${tinted.join(", ")})`;
};

/**
 * Exposes the server-configured tier colour plus pre-composited tints so the
 * SCSS never needs `color-mix` (unsupported on the older mobile browsers we
 * still serve) to paint tier-tinted card backgrounds.
 */
export const toTierCssProperties = (color: string): React.CSSProperties => {
  const rgb = hexToRgb(color);
  const channels = rgb ? `${rgb.red}, ${rgb.green}, ${rgb.blue}` : undefined;

  return {
    "--tier-color": color,
    ...(channels
      ? {
          "--tier-tint-soft": `rgba(${channels}, 0.05)`,
          "--tier-tint": `rgba(${channels}, 0.1)`,
          "--tier-tint-strong": `rgba(${channels}, 0.15)`,
          // Figma's ring track is the tier colour at 25% (13764:82468), and its
          // progress arc is the colour under 30% black like the medal's engraving.
          "--tier-tint-track": `rgba(${channels}, 0.25)`,
          "--tier-shade": shadeTierColor(color),
        }
      : {}),
  } as React.CSSProperties;
};
